import React, { FC, useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { BreakpointsInPx, Breakpoints } from '../../../../constants/breakpoints.constants';
import { iconSize } from '../../../../constants/icon.constants';
import {
  TGridCSSVariables,
  DesktopRowHeaderCellWidth,
  MobileRowHeaderCellWidth,
  DesktopSpecCellWidth,
  MobileSpecCellWidth,
  TableMaxWidth,
} from '../../../../constants/table.constants';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { windowService, documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import useStickyWrapper from '../../../../hooks/useStickyWrapper';
import useEEActive from '../../../../hooks/useEEActive';
import { googleClickAccordionEvent } from '../../../../utils/analytics';
import { TTableCompProps } from '../../types/index.types';
import ColumnHeadingElement from './columnHeadingElement';
import FixedColHeading from './fixedColHeading';
import TableHeader from './header';
import RowItems from './row';
import PrintTableContent from './printContent';
import { TableGroupModel, TableRowModel } from '../../models/index.model';
import { textContains } from '../../../../utils/regex.utils';

const SpecificationTable: FC<WithTranslation & TTableCompProps> = ({ model, t }) => {
  const GridRef = useRef(null);
  const TableWrapperRef = useRef(null);
  const [leftMostVisibleColumn, setLeftMostVisibleColumn] = useState(0);
  const [showHiddenRightCells, setShowHiddenRightCells] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fixedColumnHeading, setFixedColumnHeading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([0]);
  const [hideArrowButton, setHideArrowButton] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx.md})`);
  const stickyWrapperEle = useStickyWrapper();

  const [dropdownAtColumnIndex, setDropdownAtColumnIndex] = useState<number | null>(null);
  const [printActive, setPrintActive] = useState(false);

  // css variables
  const [gridColumns, setGridColumns] = useState('');
  const [gridScrollBy, setGridScrollBy] = useState('');
  const [gridLeftCellsMoveBy, setGridLeftCellsMoveBy] = useState('');
  const [gridFixedColHeadsMoveBy, setGridFixedColHeadsMoveBy] = useState('');
  const [gridFirstColumnWidth, setGridFirstColumnWidth] = useState('');
  const [gridColumnWidth, setGridColumnWidth] = useState('');
  const [gridOutsideSpaceHalved, setGridOutsideSpaceHalved] = useState('');
  const isEE = useEEActive();

  useEffect(() => {
    if (!GridRef?.current || !gridColumns) return;
    const gridEl = GridRef.current as HTMLElement | null;
    if (!gridEl) return;
    gridEl.style.setProperty(TGridCSSVariables.gridColumns, gridColumns);
    gridEl.style.setProperty(TGridCSSVariables.gridScrollBy, gridScrollBy);
    gridEl.style.setProperty(TGridCSSVariables.gridLeftCellsMoveBy, gridLeftCellsMoveBy);
    gridEl.style.setProperty(TGridCSSVariables.gridFixedColHeadsMoveBy, gridFixedColHeadsMoveBy);
    gridEl.style.setProperty(TGridCSSVariables.gridColumnWidth, gridColumnWidth);
    gridEl.style.setProperty(TGridCSSVariables.gridOutsideSpaceHalved, gridOutsideSpaceHalved);
  }, [
    searchTerm,
    gridColumns,
    gridScrollBy,
    gridLeftCellsMoveBy,
    gridFixedColHeadsMoveBy,
    gridColumnWidth,
    gridOutsideSpaceHalved,
    GridRef,
  ]);

  useEffect(() => {
    if (GridRef?.current && model.colHeadings.length) {
      const gridEl = GridRef.current as HTMLElement | null;
      if (!gridEl) return;
      const colLength = String(model.hideRowsHeader ? model.colHeadings.length - 1 : model.colHeadings.length);
      setGridColumns(() => colLength);
    }
  }, [GridRef, model.colHeadings]); // eslint-disable-line

  const showOrHideFixedColumn = (
    gridElDim: DOMRect,
    stickyWrapperEle: HTMLElement | null,
    topMostEmptyColHeadingDim: DOMRect,
    previousTop: number,
    fixedColShowing: boolean,
    setFixedColumnHeading: React.Dispatch<React.SetStateAction<boolean>>
  ): boolean => {
    let visibleTopLimit = stickyWrapperEle?.getBoundingClientRect().height || 0;
    let visibleBottomLimit = visibleTopLimit;
    // handling scroll down and removing the sticky row height if it is visible
    if (visibleTopLimit > 0 && gridElDim.top > previousTop && fixedColShowing) {
      visibleTopLimit -= topMostEmptyColHeadingDim.height;
      visibleBottomLimit -= topMostEmptyColHeadingDim.height;
    }

    // handling scroll up and adding buffer space of sticky row height if it is hidden
    if (gridElDim.top < previousTop && !fixedColShowing) {
      visibleBottomLimit += topMostEmptyColHeadingDim.height;
    }

    // check if table is visible and table top is above visible point and bottom is within view
    if (gridElDim.top < visibleTopLimit && gridElDim.bottom > visibleBottomLimit) {
      setFixedColumnHeading(() => true);
      return true;
    } else {
      setFixedColumnHeading(() => false);
      return false;
    }
  };

  useEffect(() => {
    let fixedColShowing = false;
    let previousTop = 0;

    function scrollHandler(): void {
      if (GridRef?.current && model.stickyHeadingColumn) {
        const gridEl = GridRef.current as HTMLElement | null;
        if (!gridEl) return;
        const gridElDim = gridEl.getBoundingClientRect();

        const topMostEmptyColHeadingDim = gridEl
          .getElementsByClassName('model-detail-specification-table__col-heading-dropdown__wrapper')?.[0]
          ?.getBoundingClientRect();

        if (!topMostEmptyColHeadingDim) return;

        fixedColShowing = showOrHideFixedColumn(
          gridElDim,
          stickyWrapperEle,
          topMostEmptyColHeadingDim,
          previousTop,
          fixedColShowing,
          setFixedColumnHeading
        );

        previousTop = gridElDim.top;
      }
    }

    documentService().addEventListener('scroll', scrollHandler);
    return (): void => {
      documentService().removeEventListener('scroll', scrollHandler);
    };
  }, [GridRef, stickyWrapperEle, model.stickyHeadingColumn]); // eslint-disable-line

  const handleHideRowsHeaderResize = (maxGridWidth: number, cellWidth: number, columnWidth: string) => {
    if (maxGridWidth - model.colHeadings.length * cellWidth > 0) {
      const newColumnWidth = maxGridWidth / model.colHeadings.length + 'px';
      setGridFirstColumnWidth(() => newColumnWidth);
      setHideArrowButton(true);
      return newColumnWidth;
    } else {
      const firstColWidth = cellWidth + 'px';
      setGridFirstColumnWidth(() => firstColWidth);
      setHideArrowButton(false);
      return columnWidth;
    }
  };

  const handleShowRowsHeaderResize = (maxGridWidth: number, cellWidth: number, headerCellWidth: number, columnWidth: string) => {
    if (maxGridWidth - model.colHeadings.length * cellWidth > headerCellWidth) {
      const newColumnWidth = (maxGridWidth - headerCellWidth) / model.colHeadings.length + 'px';
      setHideArrowButton(true);
      return newColumnWidth;
    } else {
      setHideArrowButton(false);
      return columnWidth;
    }
  };

  const handleShowOrHideRowsHeader = (maxGridWidth: number, cellWidth: number, columnWidth: string) => {
    const headerCellWidth = windowService().innerWidth >= Breakpoints.md ? DesktopRowHeaderCellWidth : MobileRowHeaderCellWidth;
    let newColumnWidth: string;
    if (model.hideRowsHeader) {
      newColumnWidth = handleHideRowsHeaderResize(maxGridWidth, cellWidth, columnWidth);
    } else {
      newColumnWidth = handleShowRowsHeaderResize(maxGridWidth, cellWidth, headerCellWidth, columnWidth);
    }
    return newColumnWidth;
  };

  const handleGridOutsideSpace = (clientWidth: number, TableMaxWidth: number) => {
    if (clientWidth && clientWidth > TableMaxWidth) {
      const remainingWidth = clientWidth - TableMaxWidth;
      setGridOutsideSpaceHalved(() => `${remainingWidth / 2}px`);
    } else {
      setGridOutsideSpaceHalved('0px');
    }
  };
  useEffect(() => {
    function resizeHandler(): void {
      if (GridRef && GridRef.current) {
        const gridEl = GridRef.current as HTMLElement | null;
        if (!gridEl) return;
        setGridScrollBy(() => '0');
        setGridLeftCellsMoveBy(() => '0');
        setGridFixedColHeadsMoveBy(() => '0');
        setLeftMostVisibleColumn(0);
        if (!model.hideAccordionTabIcon) setExpandedRows([model.groupItemsIndexes?.[0] || 0]);
        setSearchTerm('');

        const clientWidth = documentService().documentElement.clientWidth;
        const maxGridWidth = Math.min(clientWidth, TableMaxWidth);
        const cellWidth = windowService().innerWidth >= Breakpoints.md ? DesktopSpecCellWidth : MobileSpecCellWidth;
        let columnWidth = cellWidth + 'px';

        columnWidth = handleShowOrHideRowsHeader(maxGridWidth, cellWidth, columnWidth);
        setGridColumnWidth(() => columnWidth);
        handleGridOutsideSpace(clientWidth, TableMaxWidth);
      }
    }

    resizeHandler();
    windowService().addEventListener('resize', resizeHandler);
    return (): void => {
      windowService().removeEventListener('resize', resizeHandler);
    };
  }, [GridRef, model.hideRowsHeader]); // eslint-disable-line

  useEffect(() => {
    if (isDesktop && leftMostVisibleColumn > -1 && GridRef && GridRef.current && model.colHeadings.length > 0) {
      let newgridScrollBy = DesktopSpecCellWidth * leftMostVisibleColumn;
      const clientWidth = Math.min(documentService().documentElement.clientWidth, TableMaxWidth);
      const maxMove =
        (model.hideRowsHeader ? 0 : DesktopRowHeaderCellWidth) + DesktopSpecCellWidth * model.colHeadings.length - clientWidth;
      if (newgridScrollBy > maxMove && maxMove > 0) {
        newgridScrollBy = maxMove;
      }
      const gridEl = GridRef.current as HTMLElement | null;
      if (!gridEl) return;
      setGridScrollBy(() => `-${newgridScrollBy}px`);
      if (model.stickyHeadingRow) {
        setGridLeftCellsMoveBy(() => `${newgridScrollBy}px`);
      }
      setGridFixedColHeadsMoveBy(() => `-${newgridScrollBy}px`);
    }
  }, [leftMostVisibleColumn, GridRef, model.colHeadings, model.hideRowsHeader, model.stickyHeadingRow, isDesktop]);

  useEffect(() => {
    // Closing dropdown if clicked anywhere else
    function handleClick(event): void {
      if (!isDesktop) return;
      if (!event?.target?.getAttribute('data-is-dropdown-switch')) {
        setDropdownAtColumnIndex(null);
      }
    }

    const docElement = documentService();
    if (!docElement?.body) return;

    docElement.addEventListener('click', handleClick, false);
    return (): void => {
      docElement.removeEventListener('click', handleClick, false);
    };
  }, [isDesktop]);

  useEffect(() => {
    // prevent background body scrolling when model is opened in mobile view
    if (!isDesktop) {
      const docElement = documentService();
      if (!docElement?.body) return;

      if (dropdownAtColumnIndex !== null) docElement.body.classList.add('body--with-spec-model');
      else docElement.body.classList.remove('body--with-spec-model');
    }
  }, [dropdownAtColumnIndex, isDesktop]);

  useEffect(() => {
    if (!model.groupItemsIndexes || !model.items) return;
    if (model.hideAccordionTabIcon) {
      setExpandedRows(model.groupItemsIndexes);
    }
  }, [model.hideAccordionTabIcon]);

  const handleScroll = (e): void => {
    e.stopPropagation();
    if (!showHiddenRightCells) setShowHiddenRightCells(true);
    if (TableWrapperRef && TableWrapperRef.current) {
      const tableWrapperEl = TableWrapperRef.current as HTMLElement | null;
      if (!tableWrapperEl) return;
      if (tableWrapperEl.scrollLeft > -1) {
        setGridLeftCellsMoveBy(() => `${tableWrapperEl.scrollLeft}px`);
        setGridFixedColHeadsMoveBy(() => `-${tableWrapperEl.scrollLeft}px`);
      }
    }
  };

  const moveGridLeft = (): void => {
    if (GridRef && GridRef.current) {
      setLeftMostVisibleColumn((cardNo) => (cardNo > 0 ? cardNo - 1 : 0));
    }
  };

  const moveGridRight = (): void => {
    if (TableWrapperRef && TableWrapperRef.current && model.nOfVisibleColumn) {
      const tableWrapperEl = TableWrapperRef.current as HTMLElement | null;
      if (!tableWrapperEl) return;
      const availableWidth = tableWrapperEl.clientWidth - (model.hideRowsHeader ? 0 : DesktopRowHeaderCellWidth);
      const visibleColumns = Math.floor(availableWidth / DesktopSpecCellWidth);
      const maxMove = model.nOfVisibleColumn - 1 - visibleColumns;

      if (!showHiddenRightCells) setShowHiddenRightCells(true);

      setLeftMostVisibleColumn((cardNo) => (cardNo <= maxMove ? cardNo + 1 : cardNo));
    }
  };

  const toggleAllRows = (): void => {
    if (!model.groupItemsIndexes.length) return;
    if (expandedRows.length === model.groupItemsIndexes.length) {
      setExpandedRows([]);
    } else {
      setExpandedRows(model.groupItemsIndexes);
    }
  };

  const toggleRow = (index: number): void => {
    if (GridRef && GridRef.current && !model.hideAccordionTabIcon) {
      googleClickAccordionEvent(model?.items?.[index]?.title?.value, model?.heading?.value ?? model?.eyebrow?.value);

      const arrIndex = expandedRows.findIndex((val) => val === index);
      if (arrIndex > -1) {
        setExpandedRows((expandedRow) => expandedRow.filter((val) => val !== index));
      } else {
        setExpandedRows((expandedRow) => [...expandedRow, index]);
      }
    }
  };

  const findRowsInGroup = (items: TableRowModel[]): TableRowModel | undefined => {
    return items.find((item) => {
      const plainText = model.rowTitlePlainText(item?.title?.value as string);
      return textContains(plainText, searchTerm);
    });
  };

  const tableRows = useMemo(() => {
    if (searchTerm) {
      setExpandedRows([]);
      const data = model.items.filter((element, index) => {
        setExpandedRows((expandedRow) => [...expandedRow, index]);
        const plainText = model.rowTitlePlainText(element?.title?.value as string);
        if (element instanceof TableGroupModel) {
          const groupTitle = textContains(plainText, searchTerm);
          const internalRowItem = findRowsInGroup(element.items);
          return groupTitle || internalRowItem;
        } else if (element instanceof TableRowModel) {
          return textContains(plainText, searchTerm);
        }
      });
      return data;
    }
    setExpandedRows(model.groupItemsIndexes);
    return model.items;
  }, [searchTerm]);

  const printStart = useCallback(() => {
    setPrintActive(true);
  }, [setPrintActive]);

  const printEnd = useCallback(() => {
    setPrintActive(false);
  }, [setPrintActive]);

  return (
    <div
      className={`table model-details-specification-table-wrapper model-details-specification-table-wrapper--${model.backgroundColorClass}-background`}
    >
      {!model.hideHeadingSection && (
        <TableHeader
          model={model}
          isEE={isEE}
          isDesktop={isDesktop}
          toggleAllRows={toggleAllRows}
          expandedRows={expandedRows}
          hideArrowButton={hideArrowButton}
          moveGridLeft={moveGridLeft}
          moveGridRight={moveGridRight}
          searchTerm={searchTerm}
          printStart={printStart}
          setSearchTerm={setSearchTerm}
        />
      )}
      {!model.disablePrint && (
        <PrintTableContent
          printActive={printActive}
          cleanupFunction={printEnd}
          colHeadings={model.colHeadings}
          tableRows={model.items}
          isShowIconOnCell={model.showIconsOnCells}
        />
      )}
      {searchTerm && !tableRows?.length && (
        <div className="model-detail-specification-table__search-no-match">
          <Icon iconName="info-alert-notif-info" size={iconSize.xlg} extraClassName="table-info-icon" />
          <p className="model-detail-specification-table__search-no-match__title">{t('cnhi-noMatchesFoundTitle')}</p>
          <p className="model-detail-specification-table__search-no-match__desc">{t('cnhi-noMatchesFoundDescription')}</p>
        </div>
      )}
      {(!searchTerm || (searchTerm && tableRows?.length > 0)) && (
        <div
          className={`model-detail-specification-table model-detail-specification-table--${model.backgroundColorClass}-background`}
          ref={TableWrapperRef}
          onScroll={handleScroll}
        >
          <table className="model-detail-specification-table__table-tag" cellPadding="0" cellSpacing="0" ref={GridRef}>
            <FixedColHeading
              fixedColumnHeading={fixedColumnHeading}
              hideRowsHeader={model.hideRowsHeader}
              gridColumns={gridColumns}
              gridFixedColHeadsMoveBy={gridFixedColHeadsMoveBy}
              gridFirstColumnWidth={gridFirstColumnWidth}
              gridColumnWidth={gridColumnWidth}
              gridOutsideSpaceHalved={gridOutsideSpaceHalved}
              gridLeftCellsMoveBy={gridLeftCellsMoveBy}
            >
              <ColumnHeadingElement
                colHeadings={model.colHeadings}
                showHiddenRightCells={showHiddenRightCells}
                isHideImage={model?.hideImageOnHeadingRow}
              />
            </FixedColHeading>
            {!model.hideColumnHeader && (
              <tr>
                {!model.hideRowsHeader && (
                  <th className="model-detail-specification-table__left-most-cell">
                    <div className="model-detail-specification-table__left-most-empty-cell" />
                  </th>
                )}
                <ColumnHeadingElement
                  colHeadings={model.colHeadings}
                  showHiddenRightCells={showHiddenRightCells}
                  isHideImage={model?.hideImageOnHeadingRow}
                />
              </tr>
            )}
            {tableRows?.map((obj, index) => (
              <RowItems
                key={`model-detail-specification-table-${obj.title}-${Math.random()}`}
                model={model}
                obj={obj}
                toggleRow={toggleRow}
                index={index}
                expandedRows={expandedRows}
                showHiddenRightCells={showHiddenRightCells}
                searchTerm={searchTerm}
              />
            ))}
          </table>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(SpecificationTable);
